/* eslint-disable @typescript-eslint/prefer-optional-chain */
// istanbul ignore file
import React, { useEffect } from 'react'
import { CircularProgress, styled } from '@mui/material'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { type ReportData, getReactApexChartBar } from './KpiComponents/BarCharts'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import { isBubbleKpiWithBar, type KpiKeyBubble, type KpiKey } from '../redux/changeKpiType'
import { getBubbleType, getKpiIdFromName, getKpiService, getKpiTitle } from './KpiComponents/kpiUtils'
import useWindowSize from '../../../customHooks/windowResize'
import LanguageStrings from '../../../i18n/locales'
import { getDatePickerInfo } from '../../../utils/datePickerInfo'
import { fetchSpreadFactor, fetchBubbleKpiBarGraph } from '../redux/actionCreators'
import { type BarGraphParameter } from '../KpiDetails'
import { kpiColorMapDonut } from './KpiComponents/BuildKpiData'
import { type BubbleKpiState } from '../types'
import HistoryBubbleChart from './HistoryBubbleChart'
import { type BubbleKpiBarGraphState } from '../redux/historyBubbleKpiBarGraphSlice'
import { type GeneratedData, buildDonutKpiHistoryBarGraphData } from './KpiComponents/BuildKpiHistory'
import { createReportDataTemp } from './HistoryDonutKpiBarGraph'

const KpiDetailsStrings = LanguageStrings().KpiDetails

export interface KpiSeriesData {
  name: string
  data: number[]
}

export type KpiDispatchLoader = (startDate: string, endDate: string, kpiType: string, barType: string, service: string, kpiCategoryId: string) => void

export const getKpiType = (kpiType: KpiKey): string => {
  switch (kpiType) {
    case 'Spread Factor':
      return 'spread-factor-in-range'
    default:
      return 'key-not-present'
  }
}

export interface KpiHistoryState <T> {
  isLoading: boolean
  error: boolean
  data: T[] | undefined
  httpStatus: number
}

interface ParentProps {
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  setStartDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>
  setEndDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>
  selectedBubble: number
  setSelectedBubble: React.Dispatch<React.SetStateAction<number>>
  barGraphParameter: BarGraphParameter
  setBarGraphParameter: React.Dispatch<React.SetStateAction<BarGraphParameter>>
}

const WhiteDatePicker = styled(DatePicker)({
  '& input': {
    color: '#D0D0D0',
    fontSize: '14px',
    paddingRight: 0
  }
})

const CustomLocalizationProviderWrapper = styled('div')({
  backgroundColor: '#505050',
  border: 'none',
  borderRadius: '4px'
})

function HistoryBubbleKpiBarGraph (props: AllProps): JSX.Element {
  const selectedKpi = useSelector((state: RootState) => state.kpiType)
  const [barColor, setBarColor] = React.useState<string>(kpiColorMapDonut[selectedKpi as KpiKeyBubble][0])
  const initialReportData: ReportData = {
    name: '',
    type: 'bar',
    categories: [],
    series: [],
    colors: [],
    suffix: ''
  }
  const [reportData, setReportData] = React.useState<ReportData>(initialReportData)
  const { width } = useWindowSize()

  const [barChart, setBarChart] = React.useState(getReactApexChartBar(reportData, '300px', true, props.setBarGraphParameter, selectedKpi, props.barGraphParameter, props.selectedBubble))
  const [isCalled, setIsCalled] = React.useState<boolean>(false)

  useEffect(() => {
    setBarChart(getReactApexChartBar(reportData, '350px', true, props.setBarGraphParameter, selectedKpi, props.barGraphParameter, props.selectedBubble))
  }, [width, reportData, props.barGraphParameter, props.selectedBubble])

  const kpiLoader = (): KpiDispatchLoader => {
    return props.fetchBubbleKpiBarGraph
  }

  function kpiData (): GeneratedData {
    return buildDonutKpiHistoryBarGraphData(props.bubbleKpiBarGraphData)
  }

  const state = (): BubbleKpiBarGraphState => {
    return props.bubbleKpiBarGraphData
  }

  const loadKpiData = (): void => {
    const loader = kpiLoader()
    const bubbleType = getBubbleType(selectedKpi as KpiKeyBubble, props.selectedBubble, props.spreadFactor)
    loader(props.startDate.format('YYYY-MM-DD'), props.endDate.format('YYYY-MM-DD'), getKpiType(selectedKpi), bubbleType, getKpiService(selectedKpi), getKpiIdFromName(selectedKpi) ?? '')
  }

  useEffect(() => {
    if (isCalled && isBubbleKpiWithBar(selectedKpi as KpiKeyBubble) && props.spreadFactor.httpStatus === 200) {
      loadKpiData()
    }
    setIsCalled(true)
  }, [props.startDate, props.endDate, props.spreadFactor, props.selectedBubble])

  useEffect(() => {
    setReportData(createReportDataTemp(selectedKpi as KpiKeyBubble, props.barGraphParameter.bar.value, kpiData, barColor))
  }, [state().data])

  const handleDateChange = (type: string, date: dayjs.Dayjs): void => {
    if (type === 'start') {
      if (date.isAfter(props.endDate) || date.isSame(props.endDate)) {
        props.setStartDate(date)
        props.setEndDate(date)
      } else {
        props.setStartDate(date)
      }
      if (date.isAfter(props.barGraphParameter.date)) {
        props.setBarGraphParameter({ ...props.barGraphParameter, date })
      }
    } else {
      if (date.isBefore(props.startDate) || date.isSame(props.startDate)) {
        props.setEndDate(date)
        props.setStartDate(date)
      } else {
        props.setEndDate(date)
      }
      if (date.isBefore(props.barGraphParameter.date)) {
        props.setBarGraphParameter({ ...props.barGraphParameter, date })
      }
    }
  }

  const handleGraphWithFailure = (): JSX.Element => {
    const kpiState = state()
    if (kpiState.isLoading) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>
      )
    } else if (kpiState.httpStatus === 401) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (kpiState.httpStatus === 403) {
      return (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
    } else if (kpiState.httpStatus === 200 && kpiState.data === undefined) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (kpiState.data !== undefined && kpiState.httpStatus === 200) {
      return (
        barChart
      )
    } else {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={loadKpiData}/></div>
      )
    }
  }

  return (
    <div className='kpiHistoryContainer'>
      <div className='kpiHistoryHeader'>
        <span style={{ fontSize: 18 }}>{getKpiTitle(selectedKpi)}</span>
        {
          isBubbleKpiWithBar(selectedKpi as KpiKeyBubble)
            ? (
              <div style={{ display: 'flex' }}>
                <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
                  <span style={{ paddingRight: '6px', fontSize: '14px' }}>
                    {KpiDetailsStrings.StartDate}
                  </span>
                  <span className='datePicker-deviceDetails'>
                    <CustomLocalizationProviderWrapper>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <WhiteDatePicker minDate={(props.endDate ?? dayjs()).clone().subtract(3, 'month')} disableFuture slotProps={{ textField: { size: 'small' }, field: { readOnly: true } }} value={props.startDate} onAccept={(val: any) => { handleDateChange('start', val) }} />
                      </LocalizationProvider>
                    </CustomLocalizationProviderWrapper>
                  </span>
                </span>
                <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
                  <span style={{ paddingRight: '6px', fontSize: '14px' }}>
                    {KpiDetailsStrings.EndDate}
                  </span>
                  <span className='datePicker-deviceDetails'>
                    <CustomLocalizationProviderWrapper>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <WhiteDatePicker disableFuture slotProps={{ textField: { size: 'small' }, field: { readOnly: true } }} value={props.endDate} onAccept={(val: any) => { handleDateChange('end', val) }} />
                      </LocalizationProvider>
                    </CustomLocalizationProviderWrapper>
                  </span>
                  <span style={{ marginRight: '2.5rem', marginBottom: '1.5rem' }}>
                      {getDatePickerInfo()}
                  </span>
                </span>
              </div>
              )
            : <></>
        }
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
         {/* bar graph */}
        {
          isBubbleKpiWithBar(selectedKpi as KpiKeyBubble)
            ? (
            <div style={{ width: '100%' }}>
              {handleGraphWithFailure()}
            </div>
              )
            : <></>
        }
         {/* legends */}
        <HistoryBubbleChart selectedBubble={props.selectedBubble} setSelectedBubble={props.setSelectedBubble} setBarColor={setBarColor}/>
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchSpreadFactor: (kpiType: string, service: string, kpiCategoryId: string) => void
  fetchBubbleKpiBarGraph: (startDate: string, endDate: string, kpiType: string, barType: string, service: string, kpiCategoryId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchSpreadFactor: (kpiType: string, service: string, kpiCategoryId: string) => dispatch(fetchSpreadFactor(kpiType, service, kpiCategoryId)),
  fetchBubbleKpiBarGraph: (startDate: string, endDate: string, kpiType: string, barType: string, service: string, kpiCategoryId: string) => dispatch(fetchBubbleKpiBarGraph(startDate, endDate, kpiType, barType, service, kpiCategoryId))
})
interface StateToProps {
  spreadFactor: BubbleKpiState
  bubbleKpiBarGraphData: BubbleKpiBarGraphState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  spreadFactor: state.SpreadFactorState,
  bubbleKpiBarGraphData: state.bubbleKpiBarGraphData
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux
export default connector(HistoryBubbleKpiBarGraph)
